import { useState, useMemo, useCallback } from 'react';

type SelectableItemId = string | number;

export type SelectableItem = Record<string, unknown> & {
  id: SelectableItemId;
};

type SelectableItemMap = Record<SelectableItemId, boolean>;

type Selection = {
  idMap: SelectableItemMap;
  idMapForUndo: SelectableItemMap | null;
  lastSelectedId: SelectableItemId | null;
};

type SelectableItemIdExtractor = (obj: SelectableItem) => SelectableItemId;

export type UseMultiselectReturn = ReturnType<typeof useMultiselect>;

// #region A few utils for a "lookup map", i.e., an object { id => truthy value, ... }
const MapUtils = {
  fromArray: (arr: SelectableItemId[], maxSelectionCount?: number) => {
    const filteredArr = maxSelectionCount
      ? arr.slice(0, maxSelectionCount)
      : arr;

    const map: SelectableItemMap = {};
    filteredArr.forEach((x) => {
      map[x] = true;
    });
    return map;
  },

  count: (map: SelectableItemMap) =>
    Object.values(map).reduce((n, val) => (val ? n + 1 : n), 0),

  addAll: (
    map: SelectableItemMap,
    ids: SelectableItemId[],
    maxSelectionCount?: number,
  ) => {
    const newMap = { ...map };
    ids.forEach((id) => {
      newMap[id] = true;
    });

    if (maxSelectionCount && MapUtils.count(newMap) > maxSelectionCount) {
      // add new selections to the start of the array so they have priority.
      // Also ensure the new keys are strings to match the keys in the map
      const selectedIds: string[] = [
        ...ids.map((id) => `${id}`),
        ...Object.keys(newMap),
      ];
      // Removes any duplicates
      const deDupedIds = Array.from(new Set(selectedIds));

      const idsToKeep = deDupedIds.slice(0, maxSelectionCount);
      const idsToRemove = deDupedIds.filter((id) => !idsToKeep.includes(id));

      return MapUtils.removeAll(newMap, idsToRemove);
    }

    return newMap;
  },

  add: (
    map: SelectableItemMap,
    id: SelectableItemId,
    maxSelectionCount?: number,
  ) => MapUtils.addAll(map, [id], maxSelectionCount),

  removeAll: (map: SelectableItemMap, ids: SelectableItemId[]) => {
    const newMap = { ...map };
    ids.forEach((id) => {
      delete newMap[id];
    });
    return newMap;
  },

  remove: (map: SelectableItemMap, id: SelectableItemId) =>
    MapUtils.removeAll(map, [id]),

  toggle: (
    map: SelectableItemMap,
    id: SelectableItemId,
    maxSelectionCount?: number,
  ) =>
    map[id]
      ? MapUtils.remove(map, id)
      : MapUtils.add(map, id, maxSelectionCount),
};
// #endregion

const INITIAL_SELECTION: Selection = {
  idMap: {},
  idMapForUndo: null,
  lastSelectedId: null,
};

type UseMultiselectOptions = {
  selectableItems: SelectableItem[];
  idExtractor?: SelectableItemIdExtractor;
  maxSelectionCount?: number;
};

// This hook assumes that the objects are sorted when using
// toggle with a range
export const useMultiselect = ({
  selectableItems,
  idExtractor = ({ id }) => id,
  maxSelectionCount,
}: UseMultiselectOptions) => {
  const [selection, setSelection] = useState(INITIAL_SELECTION);

  const selectionCount = useMemo(
    () => MapUtils.count(selection.idMap),
    [selection],
  );

  const checkSelected = useCallback(
    (item: SelectableItem) => {
      return !!selection.idMap[idExtractor(item)];
    },
    [selection, idExtractor],
  );

  const getAllSelected = useCallback(() => {
    return selectableItems.filter(checkSelected);
  }, [selectableItems, checkSelected]);

  const canUndoSelection = !!selection.idMapForUndo;

  const undoSelection = () => {
    setSelection((prev) => ({
      idMap: prev.idMapForUndo || prev.idMap,
      idMapForUndo: null,
      lastSelectedId: null,
    }));
  };

  const clearSelection = () => {
    setSelection((prev) => ({
      idMap: {},
      idMapForUndo: prev.idMap,
      lastSelectedId: null,
    }));
  };

  const selectAll = () => {
    const allIds = selectableItems.map(idExtractor);
    setSelection((prev) => ({
      idMap: MapUtils.fromArray(allIds, maxSelectionCount),
      idMapForUndo: prev.idMap,
      lastSelectedId: null,
    }));
  };

  const selectMany = (ids: SelectableItemId[] = []) => {
    setSelection((prev) => ({
      idMap: MapUtils.fromArray(ids, maxSelectionCount),
      idMapForUndo: prev.idMap,
      lastSelectedId: null,
    }));
  };

  const toggleSelected = (object: SelectableItem, selectRange = false) => {
    const id = idExtractor(object);
    const { lastSelectedId } = selection;

    if (lastSelectedId && selectRange) {
      const startIndex = selectableItems.findIndex(
        (item) => idExtractor(item) === lastSelectedId,
      );
      const endIndex = selectableItems.findIndex(
        (item) => idExtractor(item) === id,
      );
      const ids = selectableItems
        .slice(startIndex, endIndex + 1)
        .map(idExtractor);
      setSelection((prev) => ({
        idMap: MapUtils.addAll(prev.idMap, ids, maxSelectionCount),
        idMapForUndo: null,
        lastSelectedId: id,
      }));
    } else {
      setSelection((prev) => ({
        idMap: MapUtils.toggle(prev.idMap, id, maxSelectionCount),
        idMapForUndo: null,
        lastSelectedId: id,
      }));
    }
  };

  const checkGroupSelected = (items: SelectableItem[]) => {
    return !items.some((object) => !checkSelected(object));
  };

  const toggleGroupSelected = (items: SelectableItem[]) => {
    const ids = items.map(idExtractor);
    if (checkGroupSelected(items)) {
      setSelection((prev) => ({
        idMap: MapUtils.removeAll(prev.idMap, ids),
        idMapForUndo: prev.idMap,
        lastSelectedId: null,
      }));
    } else {
      setSelection((prev) => ({
        idMap: MapUtils.addAll(prev.idMap, ids, maxSelectionCount),
        idMapForUndo: prev.idMap,
        lastSelectedId: null,
      }));
    }
  };

  return {
    checkSelected,
    selectionCount,
    getAllSelected,
    canUndoSelection,
    undoSelection,
    clearSelection,
    selectAll,
    toggleSelected,
    checkGroupSelected,
    toggleGroupSelected,
    selectMany,
  };
};
